/**
 * Area `commercial-offer` of the console gateway — over `apps/api/src/routes/commercial-offer/` (design §27.9.2). Owned by
 * plan-014 unit **E1 (read by E4)**; filled by plan-014 **E4** for Finance → Plans (design §27.15.1; FR12-CAT-02,
 * FR12-CAT-07):
 *
 *   listCommercialOffers(accountId)   `GET /v1/accounts/{account_id}/commercial-offers` — the offers this organization
 *                                     may BUY now: the public offers in force and the custom offers granted to it, each
 *                                     sellable (approved, in force, priced). An offer that is not sellable is absent —
 *                                     the console never shows a "pending" price (OD-10). At launch: pay-as-you-go only.
 *
 * AMENDED 2026-10-17 (plan-020 **WAPP-PLAN**; design §32.6.1, §32.17): the plan chooser reads the same route through
 *
 *   listOfferChoices(accountId)       the offers with what plan-020 added to each (`billing_period`, `trial_day`,
 *                                     `included_talk_second`, the presentation — names and taglines are data, never app
 *                                     copy —, the talk rates THIS organization pays per kind under the offer with their
 *                                     source, `sellable`, `refusal`) and the top-level `sale_open` (plans may be shown
 *                                     while they are not on sale: the chooser then says "Coming soon", OD-14).
 *
 * `listCommercialOffers` keeps its plan-014 answer (the account overview reads it).
 *
 * Money stays integer minor units (ADR 0014); no float crosses here.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";
import { CURRENCY_CODE, money, TALK_RATE_SOURCE, type Money, type TalkRateKind, type TalkRateSource } from "@wetalk/domain";

import { accountTalkRatesOfWire } from "../talk-rate-view.js";
import { readTalkRateKind } from "../conversation-view.js";
import type { TalkRateView } from "./talk-rate.js";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** `commercial_revision.recurrence` (E1's 0086). */
export type CommercialRecurrenceView = "payg" | "monthly" | "annual" | "custom";
const RECURRENCE: readonly CommercialRecurrenceView[] = ["payg", "monthly", "annual", "custom"];

/** One sellable offer, as Finance → Plans compares it. */
export interface CommercialOfferView {
  readonly commercialRevisionId: string;
  readonly offerCode: string;
  readonly recurrence: CommercialRecurrenceView;
  readonly eligibleScope: readonly string[];
  readonly currency: string;
  /** Net of VAT, per period; `null` exactly for pay-as-you-go. */
  readonly recurringChargeMinor: number | null;
  /**
   * Deprecated on the wire (plan-017 READ-B, PQ-8): the organization's `carrier_leg_inbound` rate now, in minor units a
   * minute — the same for every offer, because the talk price is the organization's, not the offer's. Read
   * {@link talkRates}.
   */
  readonly talkRateMinorPerMinute: number | null;
  /**
   * plan-017 (design §29.9.1): the organization's talk rates in force now — per kind its own rate, else the standard —
   * what every minute is charged at under any offer. A kind with no price is absent, never defaulted.
   */
  readonly talkRates: readonly TalkRateView[];
  /** Included quantities in native units; `null` = the offer includes none of that unit. */
  readonly included: {
    readonly talkSecond: number | null;
    readonly seat: number | null;
    readonly storageByte: number | null;
    readonly resource: Readonly<Record<string, number>>;
  };
  /** The organization's concurrency entitlement on this offer. */
  readonly channelUnit: number;
  readonly addOnEligible: readonly string[];
  readonly supportTerms: string | null;
  readonly availableUntil: string | null;
  /** A private offer granted to this organization (`custom_offer_grant`). */
  readonly custom: boolean;
  readonly grantExpiresAt: string | null;
}

/** A localised line of an offer's presentation (data, design §32.12.1 rule 6). */
export interface OfferTextView {
  readonly en: string;
  readonly el: string;
}

/** `commercial_revision_presentation` (design §32.1): how a public plan is shown. */
export interface OfferPresentationView {
  readonly displayName: OfferTextView;
  readonly tagline: OfferTextView;
  readonly displayOrder: number;
  readonly recommended: boolean;
}

/** One kind's rate a minute that THIS organization pays under an offer (own → offer → standard, OD-3). */
export interface ResolvedTalkRateView {
  readonly kind: TalkRateKind;
  readonly perMinute: Money;
  readonly source: TalkRateSource;
}

/** Why a listed offer is not sellable (`null` when it is). */
export type OfferRefusalView = "offer_term_unsupported";

/** plan-020 WAPP-PLAN: one offer as the plan chooser shows it — the plan-014 view plus plan-020's facts. */
export interface OfferChoiceView extends CommercialOfferView {
  /** `monthly` | `annual` for a plan; `null` for pay as you go and a custom offer. */
  readonly billingPeriod: "monthly" | "annual" | null;
  readonly trialDay: number | null;
  /** The talk seconds included each period (0 when none). Shown in minutes and seconds, never rounded (OA-1). */
  readonly includedTalkSecond: number;
  /** `null` for pay as you go and a custom offer. */
  readonly presentation: OfferPresentationView | null;
  /** In `TalkRateKind` order; a kind with no price is absent. */
  readonly resolvedTalkRates: readonly ResolvedTalkRateView[];
  readonly sellable: boolean;
  readonly refusal: OfferRefusalView | null;
}

/** The chooser's read: the offers and whether plans are on sale now (`commercialPolicy.subscriptionSale`). */
export interface OfferChoiceListView {
  readonly offer: readonly OfferChoiceView[];
  readonly saleOpen: boolean;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-014 E4: the offers this organization may buy now (pay-as-you-go and any custom offer granted to it). */
    listCommercialOffers(accountId: string): Promise<readonly CommercialOfferView[]>;
    /** plan-020 WAPP-PLAN: the same read with each plan's presentation and resolved rates, and `sale_open`. */
    listOfferChoices(accountId: string): Promise<OfferChoiceListView>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type CommercialOfferGatewaySlice = Pick<ConsoleGateway, "listCommercialOffers">;

/** plan-020 WAPP-PLAN: its own Pick, so the plan-014 sandbox half keeps its type; served by `../sandbox/subscription.ts`. */
export type OfferChoiceGatewaySlice = Pick<ConsoleGateway, "listOfferChoices">;

/** This area's rows of `GATEWAY_GAP`: none — `listCommercialOffers` is in E1's fragment. */
export const commercialOfferGap: readonly GatewayGap[] = [];

function wholeOrNull(value: unknown, path: string): number | null {
  return value === null || value === undefined ? null : readWholeNumber(value, path);
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

/** One offer as the API sends it (`CommercialOffer`). Exported for the sandbox half and the suites. */
export function commercialOfferOfWire(data: Readonly<Record<string, unknown>>, path: string): CommercialOfferView {
  const recurrence = RECURRENCE.find((one) => one === data["recurrence"]);
  if (recurrence === undefined) throw new Error(`${path}.recurrence is not a recurrence this console knows`);
  const price = readRecord(data["price"], `${path}.price`);
  const included = readRecord(data["included"], `${path}.included`);
  const resourceRaw = included["resource"];
  const resource: Record<string, number> = {};
  if (resourceRaw !== null && resourceRaw !== undefined) {
    for (const [kind, quantity] of Object.entries(readRecord(resourceRaw, `${path}.included.resource`))) {
      resource[kind] = readWholeNumber(quantity, `${path}.included.resource.${kind}`);
    }
  }
  return {
    commercialRevisionId: readText(data["commercial_revision_id"], `${path}.commercial_revision_id`),
    offerCode: readText(data["offer_code"], `${path}.offer_code`),
    recurrence,
    eligibleScope: readArray(data["eligible_scope"], `${path}.eligible_scope`).map((one, index) => readText(one, `${path}.eligible_scope[${index}]`)),
    currency: readText(price["currency"], `${path}.price.currency`),
    recurringChargeMinor: wholeOrNull(price["recurring_charge_minor"], `${path}.price.recurring_charge_minor`),
    talkRateMinorPerMinute: wholeOrNull(price["talk_rate_minor_per_minute"], `${path}.price.talk_rate_minor_per_minute`),
    talkRates: accountTalkRatesOfWire(price["talk_rates"], `${path}.price.talk_rates`),
    included: {
      talkSecond: wholeOrNull(included["talk_second"], `${path}.included.talk_second`),
      seat: wholeOrNull(included["seat"], `${path}.included.seat`),
      storageByte: wholeOrNull(included["storage_byte"], `${path}.included.storage_byte`),
      resource,
    },
    channelUnit: readWholeNumber(data["channel_unit"], `${path}.channel_unit`),
    addOnEligible: readArray(data["add_on_eligible"], `${path}.add_on_eligible`).map((one, index) => readText(one, `${path}.add_on_eligible[${index}]`)),
    supportTerms: textOrNull(data["support_terms"], `${path}.support_terms`),
    availableUntil: textOrNull(data["available_until"], `${path}.available_until`),
    custom: readBoolean(data["custom"], `${path}.custom`),
    grantExpiresAt: textOrNull(data["grant_expires_at"], `${path}.grant_expires_at`),
  };
}

function offerTextOf(value: unknown, path: string): OfferTextView {
  const text = readRecord(value, path);
  return { en: readText(text["en"], `${path}.en`), el: readText(text["el"], `${path}.el`) };
}

/** One `ResolvedTalkRate` row (`talk_rate_kind`, `talk_rate_minor_per_minute`, `source`). Exported for the suites. */
export function resolvedTalkRateOfWire(value: unknown, path: string): ResolvedTalkRateView {
  const row = readRecord(value, path);
  const raw = readText(row["source"], `${path}.source`);
  const source = TALK_RATE_SOURCE.find((one) => one === raw);
  if (source === undefined) throw new Error(`${path}.source is not a talk rate source this console knows`);
  return {
    kind: readTalkRateKind(row["talk_rate_kind"], `${path}.talk_rate_kind`),
    perMinute: money(readWholeNumber(row["talk_rate_minor_per_minute"], `${path}.talk_rate_minor_per_minute`), CURRENCY_CODE),
    source,
  };
}

/** One offer with plan-020's facts (`CommercialOffer`). Exported for the suites. */
export function offerChoiceOfWire(data: Readonly<Record<string, unknown>>, path: string): OfferChoiceView {
  const base = commercialOfferOfWire(data, path);
  const period = data["billing_period"];
  if (period !== null && period !== "monthly" && period !== "annual") throw new Error(`${path}.billing_period is not a billing period this console knows`);
  const refusal = data["refusal"];
  if (refusal !== null && refusal !== "offer_term_unsupported") throw new Error(`${path}.refusal is not a refusal this console knows`);
  const presentationRaw = data["presentation"];
  let presentation: OfferPresentationView | null = null;
  if (presentationRaw !== null) {
    const one = readRecord(presentationRaw, `${path}.presentation`);
    presentation = {
      displayName: offerTextOf(one["display_name"], `${path}.presentation.display_name`),
      tagline: offerTextOf(one["tagline"], `${path}.presentation.tagline`),
      displayOrder: readWholeNumber(one["display_order"], `${path}.presentation.display_order`),
      recommended: readBoolean(one["recommended"], `${path}.presentation.recommended`),
    };
  }
  return {
    ...base,
    billingPeriod: period,
    trialDay: wholeOrNull(data["trial_day"], `${path}.trial_day`),
    includedTalkSecond: readWholeNumber(data["included_talk_second"], `${path}.included_talk_second`),
    presentation,
    resolvedTalkRates: readArray(data["resolved_talk_rates"], `${path}.resolved_talk_rates`).map((one, index) =>
      resolvedTalkRateOfWire(one, `${path}.resolved_talk_rates[${String(index)}]`),
    ),
    sellable: readBoolean(data["sellable"], `${path}.sellable`),
    refusal,
  };
}

/** The list answer (`{ data, sale_open }`). Exported for the suites. */
export function offerChoiceListOfWire(body: unknown, path: string): OfferChoiceListView {
  const record = readRecord(body, path);
  return {
    offer: readArray(record["data"], `${path}.data`).map((raw, index) => offerChoiceOfWire(readRecord(raw, `${path}.data[${String(index)}]`), `${path}.data[${String(index)}]`)),
    saleOpen: readBoolean(record["sale_open"], `${path}.sale_open`),
  };
}

export function commercialOfferApiSlice(client: WeTalkApiClient, _context: SliceContext): CommercialOfferGatewaySlice & OfferChoiceGatewaySlice {
  return {
    async listCommercialOffers(accountId) {
      const body = readRecord(assertOk(await client.listCommercialOffers({ path: { account_id: accountId } })), "listCommercialOffers");
      return readArray(body["data"], "listCommercialOffers.data").map((raw, index) =>
        commercialOfferOfWire(readRecord(raw, `listCommercialOffers.data[${String(index)}]`), `listCommercialOffers.data[${String(index)}]`),
      );
    },
    async listOfferChoices(accountId) {
      return offerChoiceListOfWire(assertOk(await client.listCommercialOffers({ path: { account_id: accountId } })), "listCommercialOffers");
    },
  };
}
